import {
  CONDITION_OPS,
  FILE_KIND_LABELS,
  FILE_KINDS,
  normalizeObserver,
  POOL_MODE_LABELS,
  STEP_KIND_LABELS,
  SYSTEM_CONDITION_FIELDS,
  type SystemConditionField,
  type ApproverGroupDto,
  type ObserverDefinition,
  type PoolMode,
  type ActionDefinition,
  type Condition,
  type ConditionOp,
  type RequestTypeDto,
  type TemplateDto,
  PARTICIPANT_SOURCE_LABELS,
  type LaunchCheck,
  type ParticipantSource,
  type RouteDefinition,
  type RouteDto,
  type StageDefinition,
  type StepDefinition,
} from '@edms/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Icon } from '../lib/icons';
import { useDepartmentsList, UserSelect, useUsersList } from '../lib/tasks-ui';
import { Empty, useToast } from '../lib/ui';

export function RoutesPage() {
  const routes = useQuery({ queryKey: ['routes', 'all'], queryFn: () => api.get<RouteDto[]>('/routes', { all: true }) });
  const [editing, setEditing] = useState<RouteDto | 'new' | null>(null);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Маршруты согласования</h1>
          <p className="muted">Правки маршрута не влияют на уже запущенные согласования</p>
        </div>
        <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Link to="/approver-groups" className="btn">
            Группы согласующих
          </Link>
          <button className="btn btn-primary" onClick={() => setEditing('new')}>
            <Icon name="plus" size={18} /> Новый маршрут
          </button>
        </span>
      </div>
      {routes.data?.length === 0 && (
        <div className="card">
          <Empty emoji="🧭" title="Маршрутов пока нет">Создайте первый: например, «Договор» — руководитель, затем юрист и экономист</Empty>
        </div>
      )}
      <div className="route-cards">
        {routes.data?.map((r) => (
          <button key={r.id} className="card route-card" onClick={() => setEditing(r)}>
            <div className="section-head">
              <b>{r.name}</b>
              {!r.isActive && <span className="badge">отключён</span>}
            </div>
            {r.description && <p className="faint">{r.description}</p>}
            <div className="chain">
              {r.definition.stages.map((s, i) => (
                <span key={s.key}>
                  {i > 0 && <span className="arrow">→</span>}
                  <span className="chip">
                    {s.steps.some((st) => st.kind === 'task') ? '☑ ' : ''}
                    {s.name} · {s.steps.length}
                  </span>
                </span>
              ))}
            </div>
          </button>
        ))}
      </div>
      {editing && <RouteEditor route={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </>
  );
}

let counter = 0;
const uid = (prefix: string) => `${prefix}-${Date.now().toString(36)}${(counter++).toString(36)}`;

const emptyStep = (): StepDefinition => ({ key: uid('step'), participant: { type: 'initiator_manager' }, kind: 'approve' });
const emptyStage = (n: number): StageDefinition => ({ key: uid('stage'), name: `Этап ${n}`, mode: 'parallel', steps: [emptyStep()], onComplete: [] });

/** «definition.stages.2.steps.0.participant.groupId» → «Этап 3 «Согласование», шаг 1». */
function fieldPlace(key: string, def: RouteDefinition): string {
  const n = (re: RegExp) => {
    const m = re.exec(key);
    return m ? Number(m[1]) : null;
  };
  const parts: string[] = [];
  const stage = n(/stages\.(\d+)/);
  if (stage !== null) parts.push(`Этап ${stage + 1}${def.stages[stage]?.name ? ` «${def.stages[stage]!.name}»` : ''}`);
  const step = n(/steps\.(\d+)/);
  if (step !== null) parts.push(`шаг ${step + 1}`);
  const observer = n(/observers\.(\d+)/);
  if (observer !== null) parts.push(`наблюдатель ${observer + 1}`);
  const approved = n(/onApproved\.(\d+)/);
  if (approved !== null) parts.push(`после согласования, действие ${approved + 1}`);
  const rejected = n(/onRejected\.(\d+)/);
  if (rejected !== null) parts.push(`после отклонения, действие ${rejected + 1}`);
  const action = n(/onComplete\.(\d+)/);
  if (action !== null) parts.push(`действие ${action + 1} после этапа`);
  if (/controller/.test(key)) parts.push('контролёр');
  if (/\.when(\.|$)/.test(key)) parts.push(step !== null ? 'условие шага' : stage !== null && action === null ? 'условие этапа' : 'условие');
  return parts.length ? parts.join(', ') : key;
}

function RouteEditor({ route, onClose }: { route: RouteDto | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState(route?.name ?? '');
  const [description, setDescription] = useState(route?.description ?? '');
  const [isActive, setIsActive] = useState(route?.isActive ?? true);
  const [def, setDef] = useState<RouteDefinition>(
    () => route?.definition ?? { stages: [emptyStage(1)], observers: [], onApproved: [], onRejected: [], launchChecks: [] },
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const save = useMutation({
    mutationFn: () => {
      const body = { name, description: description || null, isActive, definition: def };
      return route ? api.patch(`/routes/${route.id}`, body) : api.post('/routes', body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['routes'] });
      toast('Маршрут сохранён');
      onClose();
    },
  });
  const err = save.error instanceof ApiError ? save.error : null;
  const { can } = useAuth();
  const remove = useMutation({
    mutationFn: () => api.delete(`/routes/${route!.id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['routes'] });
      toast('Маршрут удалён');
      onClose();
    },
    onError: (e) => toast(e.message, { error: true }),
  });

  const setStage = (i: number, patch: Partial<StageDefinition>) =>
    setDef((d) => ({ ...d, stages: d.stages.map((s, j) => (j === i ? { ...s, ...patch } : s)) }));
  const moveStage = (i: number, delta: number) =>
    setDef((d) => {
      const stages = [...d.stages];
      const [s] = stages.splice(i, 1);
      stages.splice(i + delta, 0, s!);
      return { ...d, stages };
    });

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <form
        className="drawer drawer-wide"
        role="dialog"
        aria-modal="true"
        aria-label="Маршрут"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <header>
          <h2>{route ? route.name : 'Новый маршрут'}</h2>
          <button type="button" className="btn btn-ghost btn-icon" onClick={onClose} aria-label="Закрыть">
            <Icon name="close" />
          </button>
        </header>
        <div className="body">
          {err && (
            <div className="alert">
              {err.message}
              {Object.entries(err.fields).slice(0, 5).map(([k, v]) => (
                <div key={k} className="faint">
                  {fieldPlace(k, def)}: {v}
                </div>
              ))}
            </div>
          )}
          <label className="field">
            <span>Название</span>
            <input className="input" required value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="field">
            <span>Описание (видно при выборе маршрута)</span>
            <input className="input" value={description} onChange={(e) => setDescription(e.target.value)} />
          </label>
          <label className="check">
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            <span>Маршрут доступен для новых задач</span>
          </label>

          {def.stages.map((stage, i) => (
            <fieldset key={stage.key} className="stage-edit">
              <legend>Этап {i + 1}</legend>
              <div className="stage-edit-head">
                <input className="input" value={stage.name} onChange={(e) => setStage(i, { name: e.target.value })} aria-label="Название этапа" />
                <select className="select" value={stage.mode} onChange={(e) => setStage(i, { mode: e.target.value as StageDefinition['mode'] })} aria-label="Порядок">
                  <option value="parallel">Все одновременно</option>
                  <option value="sequential">По очереди</option>
                </select>
                <span className="stage-tools">
                  <button type="button" className="btn btn-ghost btn-sm" disabled={i === 0} onClick={() => moveStage(i, -1)} aria-label="Выше">
                    ↑
                  </button>
                  <button type="button" className="btn btn-ghost btn-sm" disabled={i === def.stages.length - 1} onClick={() => moveStage(i, 1)} aria-label="Ниже">
                    ↓
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    disabled={def.stages.length === 1}
                    onClick={() => setDef((d) => ({ ...d, stages: d.stages.filter((_, j) => j !== i) }))}
                  >
                    Удалить
                  </button>
                </span>
              </div>
              {stage.steps.map((step, j) => (
                <StepEditor
                  key={step.key}
                  step={step}
                  onChange={(s) => setStage(i, { steps: stage.steps.map((x, k) => (k === j ? s : x)) })}
                  onRemove={() => setStage(i, { steps: stage.steps.filter((_, k) => k !== j) })}
                />
              ))}
              {stage.steps.length === 0 && <p className="faint">Без участников: автор добавит их сам при запуске.</p>}
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setStage(i, { steps: [...stage.steps, emptyStep()] })}>
                + участник
              </button>
              <details className="rules" open={Boolean(stage.when) || stage.onComplete.length > 0}>
                <summary>Условие этапа и действия после него</summary>
                <ConditionEditor label="Этап нужен, только если" value={stage.when} onChange={(when) => setStage(i, { when })} />
                <ActionsEditor
                  title="После этапа"
                  actions={stage.onComplete}
                  allowInsertStage
                  onChange={(onComplete) => setStage(i, { onComplete: onComplete as StageDefinition['onComplete'] })}
                />
              </details>
            </fieldset>
          ))}
          <button type="button" className="btn" onClick={() => setDef((d) => ({ ...d, stages: [...d.stages, emptyStage(d.stages.length + 1)] }))}>
            + Этап
          </button>

          <fieldset>
            <legend>Наблюдатели</legend>
            <span className="faint">Видят задачу и получают итог, но не согласуют. Наблюдателя можно добавить по условию — например, отдел кадров для договора ГПХ.</span>
            {def.observers.map((raw, i) => {
              const o = normalizeObserver(raw);
              const setO = (next: ObserverDefinition) =>
                setDef((d) => ({ ...d, observers: d.observers.map((x, k) => (k === i ? (next.when ? next : next.participant) : x)) }));
              return (
                <div key={i} className="step-edit">
                  <div className="step-edit-row">
                    <ParticipantEditor value={o.participant} onChange={(participant) => setO({ ...o, participant })} />
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDef((d) => ({ ...d, observers: d.observers.filter((_, k) => k !== i) }))} aria-label="Убрать наблюдателя">
                      ×
                    </button>
                  </div>
                  <ConditionEditor label="Только если" value={o.when} onChange={(when) => setO({ ...o, when })} />
                </div>
              );
            })}
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDef((d) => ({ ...d, observers: [...d.observers, { type: 'initiator_manager' }] }))}>
              + наблюдатель
            </button>
          </fieldset>

          <ChecksEditor checks={def.launchChecks} onChange={(launchChecks) => setDef((d) => ({ ...d, launchChecks }))} />

          <fieldset>
            <legend>Итог согласования</legend>
            <ActionsEditor title="После согласования" actions={def.onApproved} onChange={(onApproved) => setDef((d) => ({ ...d, onApproved: onApproved as RouteDefinition['onApproved'] }))} />
            <ActionsEditor title="После отказа" actions={def.onRejected} onChange={(onRejected) => setDef((d) => ({ ...d, onRejected: onRejected as RouteDefinition['onRejected'] }))} />
          </fieldset>
        </div>
        <footer>
          {route && can('records.delete') && (
            <button
              type="button"
              className="btn btn-ghost"
              style={{ marginRight: 'auto', color: 'var(--danger)' }}
              disabled={remove.isPending}
              onClick={() =>
                window.confirm(
                  `Удалить маршрут «${route.name}» навсегда?\n\nЗапущенные по нему согласования продолжатся. Черновики и типы заявок, где он выбран, останутся без маршрута. Отменить нельзя — если маршрут просто больше не нужен, снимите галочку «Активен».`,
                ) && remove.mutate()
              }
            >
              Удалить навсегда
            </button>
          )}
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Отмена
          </button>
          <button className="btn btn-primary" disabled={save.isPending || !name.trim()}>
            Сохранить
          </button>
        </footer>
      </form>
    </>
  );
}

function StepEditor({ step, onChange, onRemove }: { step: StepDefinition; onChange: (s: StepDefinition) => void; onRemove: () => void }) {
  const kind = step.kind ?? 'approve';
  const task = kind === 'task';
  return (
    <div className={`step-edit${task ? ' step-task' : ''}`}>
      <div className="step-edit-row">
        <select className="select select-kind" value={kind} onChange={(e) => onChange({ ...step, kind: e.target.value as StepDefinition['kind'] })} aria-label="Вид шага">
          {(Object.keys(STEP_KIND_LABELS) as Array<StepDefinition['kind']>).map((k) => (
            <option key={k} value={k}>
              {STEP_KIND_LABELS[k]}
            </option>
          ))}
        </select>
        <ParticipantEditor value={step.participant} onChange={(participant) => onChange({ ...step, participant })} />
        <button type="button" className="btn btn-ghost btn-sm" onClick={onRemove} aria-label="Убрать участника">
          ×
        </button>
      </div>
      <div className="step-edit-row">
        <input
          className="input"
          placeholder={task ? 'Что сделать: например, вложить скан подписанного договора' : 'Инструкция согласующему: что проверить'}
          value={step.instruction ?? ''}
          onChange={(e) => onChange({ ...step, instruction: e.target.value || undefined })}
        />
        <input
          className="input hours"
          type="number"
          min={1}
          placeholder="Срок, ч"
          value={step.deadlineHours ?? ''}
          onChange={(e) => onChange({ ...step, deadlineHours: e.target.value ? Number(e.target.value) : undefined })}
          aria-label="Срок в часах"
          title="Срок в часах: 48 — два дня"
        />
      </div>
      {task && (
        <div className="step-edit-row wrap">
          <input
            className="input"
            placeholder="Надпись на кнопке: «Скан вложен»"
            value={step.actionLabel ?? ''}
            maxLength={60}
            onChange={(e) => onChange({ ...step, actionLabel: e.target.value || undefined })}
            aria-label="Надпись на кнопке"
          />
          <select
            className="select"
            value={step.requireFile ?? ''}
            onChange={(e) => onChange({ ...step, requireFile: (e.target.value || undefined) as StepDefinition['requireFile'] })}
            aria-label="Нужен файл"
          >
            <option value="">файл не обязателен</option>
            {FILE_KINDS.map((k) => (
              <option key={k} value={k}>
                нужен: {FILE_KIND_LABELS[k].toLowerCase()}
              </option>
            ))}
          </select>
          <label className="check">
            <input type="checkbox" checked={Boolean(step.requireComment)} onChange={(e) => onChange({ ...step, requireComment: e.target.checked || undefined })} />
            <span>комментарий обязателен</span>
          </label>
        </div>
      )}
      <FillFieldsEditor value={step.fillFields ?? []} onChange={(fillFields) => onChange({ ...step, fillFields: fillFields.length ? fillFields : undefined })} />
      <details className="rules" open={Boolean(step.controller || step.when)}>
        <summary>Контроль и условие шага</summary>
        <div className="step-edit-row wrap">
          <label className="check">
            <input
              type="checkbox"
              checked={Boolean(step.controller)}
              onChange={(e) => onChange({ ...step, controller: e.target.checked ? { type: 'initiator_manager' } : undefined })}
            />
            <span>Контролёр</span>
          </label>
          {step.controller && <ParticipantEditor value={step.controller} onChange={(controller) => onChange({ ...step, controller })} single />}
        </div>
        {step.controller && <span className="faint">Контролёр получит уведомления: когда шаг начнётся, если срок нарушен и когда выполнено.</span>}
        <ConditionEditor label="Шаг нужен, только если" value={step.when} onChange={(when) => onChange({ ...step, when })} />
      </details>
    </div>
  );
}

function useGroups() {
  return useQuery({ queryKey: ['approver-groups'], queryFn: () => api.get<ApproverGroupDto[]>('/approver-groups'), staleTime: 60_000 });
}

/** single — нужен один человек (контролёр, исполнитель подзадачи): без «каждый из». */
function ParticipantEditor({ value, onChange, single }: { value: ParticipantSource; onChange: (v: ParticipantSource) => void; single?: boolean }) {
  const users = useUsersList();
  const departments = useDepartmentsList();
  const groups = useGroups();
  const setType = (type: ParticipantSource['type']) => {
    switch (type) {
      case 'user':
        return onChange({ type, userId: users.data?.[0]?.id ?? '' });
      case 'department_head':
        return onChange({ type, departmentId: departments.data?.[0]?.id ?? '' });
      case 'department':
        return onChange({ type, departmentId: departments.data?.[0]?.id ?? '', mode: 'any' });
      case 'group':
        return onChange({ type, groupId: groups.data?.[0]?.id ?? '', mode: 'any' });
      case 'form_field':
        return onChange({ type, field: 'executor' });
      case 'all_users':
        return onChange({ type, mode: 'all' });
      default:
        return onChange({ type });
    }
  };
  const modeSelect = (mode: PoolMode, set: (m: PoolMode) => void) =>
    single ? null : (
      <select className="select" value={mode} onChange={(e) => set(e.target.value as PoolMode)} aria-label="Кто согласует">
        {(Object.keys(POOL_MODE_LABELS) as PoolMode[]).map((m) => (
          <option key={m} value={m}>
            {POOL_MODE_LABELS[m]}
          </option>
        ))}
      </select>
    );
  return (
    <span className="participant-edit">
      <select className="select" value={value.type} onChange={(e) => setType(e.target.value as ParticipantSource['type'])} aria-label="Кто">
        {Object.entries(PARTICIPANT_SOURCE_LABELS)
          // Контролёр и исполнитель подзадачи — один человек, «все сотрудники» им не подходят.
          .filter(([k]) => !(single && k === 'all_users'))
          .map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
      </select>
      {value.type === 'user' && <UserSelect value={value.userId} onChange={(userId) => onChange({ type: 'user', userId })} invalid={!value.userId} />}
      {(value.type === 'department_head' || value.type === 'department') && (
        <select className="select" value={value.departmentId} onChange={(e) => onChange({ ...value, departmentId: e.target.value })} aria-label="Отдел" aria-invalid={!value.departmentId}>
          <option value="">{departments.isPending ? 'Загрузка…' : 'Выберите отдел'}</option>
          {departments.data?.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </select>
      )}
      {value.type === 'department' && modeSelect(value.mode, (mode) => onChange({ ...value, mode }))}
      {value.type === 'group' && (
        <>
          <select className="select" value={value.groupId} onChange={(e) => onChange({ ...value, groupId: e.target.value })} aria-label="Группа" aria-invalid={!value.groupId}>
            <option value="">{groups.isPending ? 'Загрузка…' : groups.data?.length === 0 ? 'Групп пока нет — создайте в «Группы согласующих»' : 'Выберите группу'}</option>
            {groups.data?.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name} ({g.members.length})
              </option>
            ))}
          </select>
          {modeSelect(value.mode, (mode) => onChange({ ...value, mode }))}
        </>
      )}
      {value.type === 'form_field' && (
        <input className="input" value={value.field} onChange={(e) => onChange({ type: 'form_field', field: e.target.value })} aria-label="Поле заявки" />
      )}
      {value.type === 'all_users' && <span className="faint">каждый, кроме автора; состав — на момент запуска</span>}
    </span>
  );
}

/** Проверки перед запуском: «нужен скан», «связанный договор согласован и со сканом». */
function ChecksEditor({ checks, onChange }: { checks: LaunchCheck[]; onChange: (c: LaunchCheck[]) => void }) {
  // Поле выбирается из «Связанных документов» типов заявок: опечатка в ключе ломала проверку.
  const docFields = useTypedFields(['document']);
  const set = (i: number, patch: Partial<LaunchCheck>) => onChange(checks.map((c, j) => (j === i ? ({ ...c, ...patch } as LaunchCheck) : c)));
  return (
    <fieldset>
      <legend>Проверки перед запуском</legend>
      <span className="faint">Без выполнения проверки согласование не запустится. Снимите галочку «действует», чтобы временно отключить.</span>
      {checks.map((c, i) => (
        <div key={i} className="step-edit">
          <div className="step-edit-row">
            <b style={{ flex: 1 }}>{c.type === 'required_file' ? 'Нужен файл' : 'Связанный документ'}</b>
            <label className="check">
              <input type="checkbox" checked={c.enabled} onChange={(e) => set(i, { enabled: e.target.checked })} />
              <span>действует</span>
            </label>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => onChange(checks.filter((_, j) => j !== i))} aria-label="Удалить проверку">
              ×
            </button>
          </div>
          <div className="step-edit-row">
            <input className="input" value={c.label} onChange={(e) => set(i, { label: e.target.value })} aria-label="Название проверки" placeholder="Название" />
            {c.type === 'required_file' ? (
              <select className="select" value={c.kind} onChange={(e) => set(i, { kind: e.target.value as (typeof FILE_KINDS)[number] })} aria-label="Вид файла">
                {FILE_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {FILE_KIND_LABELS[k]}
                  </option>
                ))}
              </select>
            ) : (
              <select className="select" value={c.field} onChange={(e) => set(i, { field: e.target.value })} aria-label="Поле" aria-invalid={!docFields.some((f) => f.key === c.field)}>
                {!docFields.some((f) => f.key === c.field) && <option value={c.field}>{c.field ? `«${c.field}» — такого поля нет` : 'Выберите поле'}</option>}
                {docFields.map((f) => (
                  <option key={f.key} value={f.key}>
                    {f.label}
                  </option>
                ))}
              </select>
            )}
          </div>
          {c.type === 'linked_document' && (
            <div className="step-edit-row">
              <label className="check">
                <input type="checkbox" checked={c.requireApproved} onChange={(e) => set(i, { requireApproved: e.target.checked })} />
                <span>должен быть согласован</span>
              </label>
              <label className="check">
                <input type="checkbox" checked={c.requireScan} onChange={(e) => set(i, { requireScan: e.target.checked })} />
                <span>должен быть скан</span>
              </label>
              <label className="check" title="Например, для договоров, заключённых до перехода на систему: их нет в списке">
                <input type="checkbox" checked={c.skipIfEmpty} onChange={(e) => set(i, { skipIfEmpty: e.target.checked })} />
                <span>можно не выбирать — проверять, только если выбран</span>
              </label>
            </div>
          )}
        </div>
      ))}
      <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => onChange([...checks, { type: 'required_file', kind: 'scan', label: 'Скан', enabled: true }])}>
          + нужен файл
        </button>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() =>
            onChange([...checks, { type: 'linked_document', field: docFields[0]?.key ?? '', label: 'Договор', requireApproved: true, requireScan: true, skipIfEmpty: false, enabled: true }])
          }
        >
          + связанный документ
        </button>
      </span>
    </fieldset>
  );
}

// ---------------------------------------------------------------------------
// Условия
// ---------------------------------------------------------------------------

const OP_LABELS: Record<ConditionOp, string> = {
  eq: 'равно',
  neq: 'не равно',
  gt: 'больше',
  gte: 'не меньше',
  lt: 'меньше',
  lte: 'не больше',
  in: 'одно из',
  not_in: 'ни одно из',
  empty: 'не заполнено',
  not_empty: 'заполнено',
};

type Rule = { field: string; op: ConditionOp; value?: string | number | boolean | null | Array<string | number> };

/** Условие «все правила» / «любое из правил». Вложенные условия показываются как есть и не теряются. */
function toRules(c: Condition | undefined): { mode: 'all' | 'any'; rules: Rule[] } | null {
  if (!c) return { mode: 'all', rules: [] };
  if ('field' in c) return { mode: 'all', rules: [c] };
  if ('all' in c && c.all.every((x) => 'field' in x)) return { mode: 'all', rules: c.all as Rule[] };
  if ('any' in c && c.any.every((x) => 'field' in x)) return { mode: 'any', rules: c.any as Rule[] };
  return null;
}

function fromRules(mode: 'all' | 'any', rules: Rule[]): Condition | undefined {
  if (rules.length === 0) return undefined;
  if (rules.length === 1) return rules[0];
  return mode === 'all' ? { all: rules } : { any: rules };
}

function parseValue(op: ConditionOp, raw: string): Rule['value'] {
  const one = (x: string) => (x.trim() !== '' && Number.isFinite(Number(x.replace(',', '.'))) ? Number(x.replace(',', '.')) : x.trim());
  if (op === 'in' || op === 'not_in') return raw.split(';').map((x) => one(x)).filter((x) => x !== '');
  return one(raw);
}

/** Поля заявок, которые можно поручить заполнить участнику шага (расчётные — нельзя). */
function useFillableFields(): Array<{ key: string; label: string }> {
  const types = useQuery({ queryKey: ['request-types', 'all'], queryFn: () => api.get<RequestTypeDto[]>('/request-types', { all: true }), staleTime: 60_000 });
  const map = new Map<string, string>();
  for (const t of types.data ?? []) for (const f of t.fields) if (!f.system) map.set(f.key, `${f.label} (${t.name})`);
  return [...map.entries()].map(([key, label]) => ({ key, label }));
}

/** «Заполнить поля»: без них участник не согласует шаг; значения попадут в заявку. */
function FillFieldsEditor({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const options = useFillableFields();
  const label = (k: string) => options.find((o) => o.key === k)?.label ?? k;
  return (
    <div className="fill-fields">
      <span className="faint">Заполнить поля:</span>
      {value.map((k) => (
        <span key={k} className="chip">
          {label(k)}
          <button type="button" className="chip-x" onClick={() => onChange(value.filter((x) => x !== k))} aria-label={`Убрать ${label(k)}`}>
            ×
          </button>
        </span>
      ))}
      <select className="select select-inline" value="" onChange={(e) => e.target.value && onChange([...value, e.target.value])} aria-label="Добавить поле">
        <option value="">{value.length ? '+ ещё поле' : options.length ? '+ поле заявки' : 'нет полей в типах заявок'}</option>
        {options
          .filter((o) => !value.includes(o.key))
          .map((o) => (
            <option key={o.key} value={o.key}>
              {o.label}
            </option>
          ))}
      </select>
    </div>
  );
}

/** Поля заявок нужного вида (сотрудник, дата) — для привязки действия к полям. */
function useTypedFields(kinds: string[]): Array<{ key: string; label: string }> {
  const types = useQuery({ queryKey: ['request-types', 'all'], queryFn: () => api.get<RequestTypeDto[]>('/request-types', { all: true }), staleTime: 60_000 });
  const map = new Map<string, string[]>();
  for (const t of types.data ?? []) {
    for (const f of t.fields) {
      if (!kinds.includes(f.type)) continue;
      map.set(f.key, [...(map.get(f.key) ?? []), `${f.label} (${t.name})`]);
    }
  }
  return [...map.entries()].map(([key, labels]) => ({ key, label: labels.length > 2 ? `${labels.slice(0, 2).join(', ')} и ещё ${labels.length - 2}` : labels.join(', ') }));
}

type SubstitutionAction = Extract<AnyAction, { type: 'create_substitution' }>;

function FieldPick({ label, value, kinds, empty, onChange }: { label: string; value: string | undefined; kinds: string[]; empty?: string; onChange: (v: string | undefined) => void }) {
  const options = useTypedFields(kinds);
  const known = !value || options.some((o) => o.key === value);
  return (
    <label className="field-pick">
      <span className="faint">{label}</span>
      <select className="select" value={value ?? ''} onChange={(e) => onChange(e.target.value || undefined)} aria-label={label} aria-invalid={!empty && !value}>
        <option value="">{empty ?? 'Выберите поле'}</option>
        {!known && <option value={value}>{value} (нет в типах заявок)</option>}
        {options.map((o) => (
          <option key={o.key} value={o.key}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/** «Назначить замещение»: из каких полей заявки взять сотрудника, заместителя и даты. */
function SubstitutionActionEditor({ action, onChange }: { action: SubstitutionAction; onChange: (a: SubstitutionAction) => void }) {
  return (
    <div className="substitution-action">
      <FieldPick label="Кого замещают" value={action.absentField} kinds={['user']} empty="Инициатора" onChange={(absentField) => onChange({ ...action, absentField })} />
      <FieldPick label="Заместитель" value={action.deputyField} kinds={['user']} onChange={(v) => onChange({ ...action, deputyField: v ?? '' })} />
      <FieldPick label="С" value={action.dateFromField} kinds={['date']} onChange={(v) => onChange({ ...action, dateFromField: v ?? '' })} />
      <FieldPick label="По" value={action.dateToField} kinds={['date']} onChange={(v) => onChange({ ...action, dateToField: v ?? '' })} />
      <FieldPick label="Причина" value={action.reasonField} kinds={['text', 'textarea', 'select']} empty="Без причины" onChange={(reasonField) => onChange({ ...action, reasonField })} />
      <span className="faint">
        Замещение появится в разделе «Замещения» и в профиле сотрудника: в эти даты заместитель видит и согласует задачи отсутствующего. Если поле не заполнено, в истории задачи будет запись, почему замещение не назначено.
      </span>
    </div>
  );
}

function useFieldKeys(): Array<{ key: string; label: string }> {
  const types = useQuery({ queryKey: ['request-types', 'all'], queryFn: () => api.get<RequestTypeDto[]>('/request-types', { all: true }), staleTime: 60_000 });
  const map = new Map<string, string>();
  for (const t of types.data ?? []) {
    for (const f of t.fields) if (f.type !== 'table') map.set(f.key, `${f.label} (${t.name})`);
    for (const c of t.computed) map.set(c.key, `${c.label} (${t.name})`);
  }
  return [...map.entries()].map(([key, label]) => ({ key, label }));
}

const isSystemField = (f: string): f is SystemConditionField => f in SYSTEM_CONDITION_FIELDS;
const SYSTEM_OPS: ConditionOp[] = ['not_in', 'in', 'eq', 'neq'];

/** Значение условия по инициатору или его отделу — выбор из списка, а не ввод кода. */
function SystemRuleValue({ rule, onChange }: { rule: Rule; onChange: (value: Rule['value']) => void }) {
  const users = useUsersList();
  const departments = useDepartmentsList();
  const isUser = rule.field === '_initiator';
  const options = isUser
    ? (users.data ?? []).map((u) => ({ id: u.id, name: u.fullName }))
    : (departments.data ?? []).map((d) => ({ id: d.id, name: d.name }));
  const nameOf = (id: string) => options.find((o) => o.id === id)?.name ?? '…';
  const placeholder = isUser ? 'Выберите сотрудника' : 'Выберите отдел';

  if (rule.op === 'eq' || rule.op === 'neq') {
    const value = typeof rule.value === 'string' ? rule.value : '';
    return (
      <select className="select" value={value} onChange={(e) => onChange(e.target.value)} aria-label="Значение" aria-invalid={!value}>
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
          </option>
        ))}
      </select>
    );
  }
  const list = Array.isArray(rule.value) ? rule.value.map(String) : [];
  return (
    <div className="rule-pick">
      {list.map((id) => (
        <span key={id} className="chip">
          {nameOf(id)}
          <button type="button" className="chip-x" onClick={() => onChange(list.filter((x) => x !== id))} aria-label={`Убрать ${nameOf(id)}`}>
            ×
          </button>
        </span>
      ))}
      <select className="select" value="" onChange={(e) => e.target.value && onChange([...list, e.target.value])} aria-label="Добавить" aria-invalid={list.length === 0}>
        <option value="">{list.length ? '+ ещё' : placeholder}</option>
        {options
          .filter((o) => !list.includes(o.id))
          .map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
      </select>
    </div>
  );
}

function ConditionEditor({ label, value, onChange }: { label: string; value: Condition | undefined; onChange: (c: Condition | undefined) => void }) {
  const parsed = toRules(value);
  const keys = useFieldKeys();
  if (!parsed) {
    return (
      <div className="faint">
        {label}: сложное условие ({JSON.stringify(value)}).{' '}
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => onChange(undefined)}>
          Сбросить
        </button>
      </div>
    );
  }
  const { mode, rules } = parsed;
  const set = (i: number, patch: Partial<Rule>) => onChange(fromRules(mode, rules.map((r, j) => (j === i ? { ...r, ...patch } : r))));
  return (
    <div className="condition">
      <div className="condition-head">
        <span>{label}</span>
        {rules.length > 1 && (
          <select className="select select-inline" value={mode} onChange={(e) => onChange(fromRules(e.target.value as 'all' | 'any', rules))} aria-label="Сочетание">
            <option value="all">выполнены все условия</option>
            <option value="any">выполнено любое</option>
          </select>
        )}
        {rules.length === 0 && <span className="faint">всегда</span>}
      </div>
      <datalist id="field-keys">
        {Object.entries(SYSTEM_CONDITION_FIELDS).map(([k, l]) => (
          <option key={k} value={k}>
            {l}
          </option>
        ))}
        {keys.map((k) => (
          <option key={k.key} value={k.key}>
            {k.label}
          </option>
        ))}
      </datalist>
      {rules.map((r, i) => (
        <div key={i} className="rule-row">
          {isSystemField(r.field) ? (
            <select
              className="select"
              value={r.field}
              onChange={(e) => set(i, e.target.value ? { field: e.target.value, value: r.op === 'eq' || r.op === 'neq' ? '' : [] } : { field: '', op: 'gt', value: 0 })}
              aria-label="Поле"
            >
              {Object.entries(SYSTEM_CONDITION_FIELDS).map(([k, l]) => (
                <option key={k} value={k}>
                  {l}
                </option>
              ))}
              <option value="">поле заявки…</option>
            </select>
          ) : (
            <input
              className="input mono"
              list="field-keys"
              placeholder="поле, напр. total"
              value={r.field}
              onChange={(e) => set(i, isSystemField(e.target.value) ? { field: e.target.value, op: 'not_in', value: [] } : { field: e.target.value })}
              aria-label="Поле"
            />
          )}
          <select
            className="select"
            value={r.op}
            onChange={(e) => {
              const op = e.target.value as ConditionOp;
              const single = op === 'eq' || op === 'neq';
              // У инициатора значение — один id или список: при смене вида сравнения переводим.
              if (isSystemField(r.field) && single !== (r.op === 'eq' || r.op === 'neq')) {
                const list = Array.isArray(r.value) ? r.value.map(String) : typeof r.value === 'string' && r.value ? [r.value] : [];
                set(i, { op, value: single ? (list[0] ?? '') : list });
              } else set(i, { op });
            }}
            aria-label="Сравнение"
          >
            {(isSystemField(r.field) ? SYSTEM_OPS : CONDITION_OPS).map((op) => (
              <option key={op} value={op}>
                {OP_LABELS[op]}
              </option>
            ))}
          </select>
          {isSystemField(r.field) && <SystemRuleValue rule={r} onChange={(value) => set(i, { value })} />}
          {!isSystemField(r.field) && r.op !== 'empty' && r.op !== 'not_empty' && (
            <input
              className="input"
              placeholder={r.op === 'in' || r.op === 'not_in' ? 'значения через ;' : 'значение'}
              defaultValue={Array.isArray(r.value) ? r.value.join('; ') : r.value === undefined || r.value === null ? '' : String(r.value)}
              onBlur={(e) => set(i, { value: parseValue(r.op, e.target.value) })}
              aria-label="Значение"
            />
          )}
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => onChange(fromRules(mode, rules.filter((_, j) => j !== i)))} aria-label="Убрать условие">
            ×
          </button>
        </div>
      ))}
      <div className="condition-add">
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => onChange(fromRules(mode, [...rules, { field: '', op: 'gt', value: 0 }]))}>
          + условие по полю
        </button>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => onChange(fromRules(mode, [...rules, { field: '_initiator', op: 'not_in', value: [] }]))}>
          + по инициатору
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Действия
// ---------------------------------------------------------------------------

type AnyAction = ActionDefinition;

const ACTION_LABELS: Record<AnyAction['type'], string> = {
  notify: 'Уведомить',
  webhook: 'Отправить данные во внешний сервис',
  create_subtask: 'Поставить подзадачу',
  generate_document: 'Сформировать документ по шаблону',
  approval_sheet: 'Сформировать лист согласования',
  add_observer: 'Добавить наблюдателя',
  create_substitution: 'Назначить замещение',
  insert_stage: 'Добавить этап',
};

const TARGETS: Array<{ value: 'initiator' | 'observers' | 'participants' | 'stage_participants'; label: string }> = [
  { value: 'initiator', label: 'инициатора' },
  { value: 'observers', label: 'наблюдателей' },
  { value: 'participants', label: 'всех участников' },
  { value: 'stage_participants', label: 'участников этапа' },
];

function newAction(type: AnyAction['type'], templateId: string | undefined): AnyAction {
  switch (type) {
    case 'notify':
      return { type, to: ['initiator'], message: '' };
    case 'webhook':
      return { type, url: 'https://' };
    case 'create_subtask':
      return { type, title: '', assignee: { type: 'initiator' } };
    case 'generate_document':
      return { type, templateId: templateId ?? '' };
    case 'approval_sheet':
      return { type };
    case 'add_observer':
      return { type, participant: { type: 'initiator_manager' } };
    case 'create_substitution':
      return { type, absentField: 'employee', deputyField: 'deputy', dateFromField: 'dateFrom', dateToField: 'dateTo', reasonField: 'reason' };
    case 'insert_stage':
      return { type, stage: { key: uid('stage'), name: 'Дополнительный этап', mode: 'parallel', steps: [emptyStep()], onComplete: [] } };
  }
}

function ActionsEditor({ title, actions, onChange, allowInsertStage }: { title: string; actions: AnyAction[]; onChange: (a: AnyAction[]) => void; allowInsertStage?: boolean }) {
  const templates = useQuery({ queryKey: ['templates', 'all'], queryFn: () => api.get<TemplateDto[]>('/templates', { all: true }), staleTime: 60_000 });
  const set = (i: number, a: AnyAction) => onChange(actions.map((x, j) => (j === i ? a : x)));
  const types = (Object.keys(ACTION_LABELS) as Array<AnyAction['type']>).filter((t) => allowInsertStage || t !== 'insert_stage');
  return (
    <div className="actions">
      <span className="actions-title">{title}</span>
      {actions.map((a, i) => (
        <div key={i} className="action-card">
          <div className="step-edit-row">
            <b style={{ flex: 1 }}>{ACTION_LABELS[a.type]}</b>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => onChange(actions.filter((_, j) => j !== i))} aria-label="Убрать действие">
              ×
            </button>
          </div>
          {a.type === 'notify' && (
            <>
              <div className="chain">
                {TARGETS.map((t) => (
                  <label key={t.value} className="check">
                    <input
                      type="checkbox"
                      checked={a.to.includes(t.value)}
                      onChange={(e) => set(i, { ...a, to: e.target.checked ? [...a.to, t.value] : a.to.filter((x) => x !== t.value) })}
                    />
                    <span>{t.label}</span>
                  </label>
                ))}
              </div>
              <input className="input" placeholder="Текст уведомления" value={a.message} onChange={(e) => set(i, { ...a, message: e.target.value })} aria-label="Текст" />
            </>
          )}
          {a.type === 'webhook' && (
            <input className="input mono" placeholder="https://erp.example.ru/hook" value={a.url} onChange={(e) => set(i, { ...a, url: e.target.value })} aria-label="Адрес" />
          )}
          {a.type === 'create_subtask' && (
            <>
              <input className="input" placeholder="Что сделать" value={a.title} onChange={(e) => set(i, { ...a, title: e.target.value })} aria-label="Подзадача" />
              <div className="step-edit-row">
                <ParticipantEditor value={a.assignee} onChange={(assignee) => set(i, { ...a, assignee })} single />
                <input
                  className="input hours"
                  type="number"
                  min={1}
                  placeholder="Срок, ч"
                  value={a.deadlineHours ?? ''}
                  onChange={(e) => set(i, { ...a, deadlineHours: e.target.value ? Number(e.target.value) : undefined })}
                  aria-label="Срок"
                />
              </div>
            </>
          )}
          {a.type === 'generate_document' && (
            <select className="select" value={a.templateId} onChange={(e) => set(i, { ...a, templateId: e.target.value })} aria-label="Шаблон">
              <option value="">Выберите шаблон</option>
              {templates.data?.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          )}
          {a.type === 'approval_sheet' && (
            <span className="faint">PDF с решениями, принятыми к этому моменту. Если маршрут продолжается задачами (скан, регистрация), лист не ждёт их.</span>
          )}
          {a.type === 'create_substitution' && <SubstitutionActionEditor action={a} onChange={(next) => set(i, next)} />}
          {a.type === 'add_observer' && <ParticipantEditor value={a.participant} onChange={(participant) => set(i, { ...a, participant })} />}
          {a.type === 'insert_stage' && (
            <div className="step-edit">
              <div className="stage-edit-head">
                <input className="input" value={a.stage.name} onChange={(e) => set(i, { ...a, stage: { ...a.stage, name: e.target.value } })} aria-label="Название этапа" />
                <select className="select" value={a.stage.mode} onChange={(e) => set(i, { ...a, stage: { ...a.stage, mode: e.target.value as StageDefinition['mode'] } })} aria-label="Порядок">
                  <option value="parallel">Все одновременно</option>
                  <option value="sequential">По очереди</option>
                </select>
              </div>
              {a.stage.steps.map((st, j) => (
                <StepEditor
                  key={st.key}
                  step={st}
                  onChange={(next) => set(i, { ...a, stage: { ...a.stage, steps: a.stage.steps.map((x, k) => (k === j ? next : x)) } })}
                  onRemove={() => set(i, { ...a, stage: { ...a.stage, steps: a.stage.steps.filter((_, k) => k !== j) } })}
                />
              ))}
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => set(i, { ...a, stage: { ...a.stage, steps: [...a.stage.steps, emptyStep()] } })}>
                + участник
              </button>
            </div>
          )}
          <ConditionEditor label="Только если" value={a.when} onChange={(when) => set(i, { ...a, when } as AnyAction)} />
        </div>
      ))}
      <select
        className="select select-inline"
        value=""
        onChange={(e) => e.target.value && onChange([...actions, newAction(e.target.value as AnyAction['type'], templates.data?.[0]?.id)])}
        aria-label="Добавить действие"
      >
        <option value="">+ действие…</option>
        {types.map((t) => (
          <option key={t} value={t}>
            {ACTION_LABELS[t]}
          </option>
        ))}
      </select>
    </div>
  );
}
